<template>
  <div class="su">
    <div class="su-title"><i class="fe fe-repeat mr-1"></i>This patient's use of this {{ u ? u.kind : "item" }}</div>
    <div v-if="loading" class="su-muted"><i class="mdi mdi-loading mdi-spin"></i> Loading…</div>
    <div v-else-if="error" class="su-muted"><i class="fe fe-alert-triangle"></i> {{ error }}</div>
    <div v-else-if="!u" class="su-muted">No usage found for this line.</div>
    <template v-else>
      <div class="su-stats">
        <div class="su-stat su-stat--year">
          <span class="su-label">In {{ year }}</span>
          <span class="su-num">{{ u.this_year.times }}<small>×</small></span>
          <span class="su-sub">{{ u.this_year.other_times }} besides this claim</span>
        </div>
        <div class="su-stat">
          <span class="su-label">All time</span>
          <span class="su-num">{{ u.all_time.times }}<small>×</small></span>
          <span class="su-sub">{{ u.all_time.other_times }} besides this claim</span>
        </div>
      </div>

      <table class="su-table">
        <thead><tr><th></th><th>In {{ year }}</th><th>All time</th></tr></thead>
        <tbody>
          <tr><td>On claims</td><td>{{ u.this_year.on_claims }}</td><td>{{ u.all_time.on_claims }}</td></tr>
          <tr><td>At encounters</td><td>{{ u.this_year.at_encounters }}</td><td>{{ u.all_time.at_encounters }}</td></tr>
          <tr><td>Quantity</td><td>{{ u.this_year.quantity }}</td><td>{{ u.all_time.quantity }}</td></tr>
          <tr><td>Amount</td><td>{{ money(u.this_year.amount) }}</td><td>{{ money(u.all_time.amount) }}</td></tr>
          <tr><td>Facilities</td><td>{{ u.this_year.facilities }}</td><td>{{ u.all_time.facilities }}</td></tr>
        </tbody>
      </table>

      <div v-if="u.last_30_days_before" class="su-flag su-flag--warn">
        <i class="fe fe-alert-triangle"></i>
        <span>Also given <b>{{ u.last_30_days_before }}×</b> in the 30 days before this claim.</span>
      </div>
      <div v-if="u.last_before" class="su-flag">
        <i class="fe fe-clock"></i>
        <span>Last given <b>{{ u.last_before.days_before === 0 ? "the same day" : `${u.last_before.days_before} days before` }}</b>
          ({{ fmt(u.last_before.date) }}{{ u.last_before.facility ? ", " + u.last_before.facility : "" }}, {{ u.last_before.source }}).</span>
      </div>
      <div v-else class="su-flag su-flag--ok"><i class="fe fe-check-circle"></i><span>Not given to this patient before this claim.</span></div>

      <div v-if="years.length > 1" class="su-years" aria-label="Times per year">
        <div v-for="y in years" :key="y.year" class="su-year" :title="`${y.n} in ${y.year}`">
          <span class="su-year-bar" :style="{ height: Math.max(4, (y.n / yearMax) * 36) + 'px' }" :class="{ 'su-year-bar--now': String(y.year) === String(year) }"></span>
          <small>{{ String(y.year).slice(2) }}</small>
        </div>
      </div>

      <div class="su-sub-title">Latest uses</div>
      <ul class="su-timeline">
        <li v-for="(r, i) in u.recent" :key="i" :class="{ 'is-this': r.is_this_claim }">
          <span class="su-dot" :class="r.source === 'claim' ? 'su-dot--claim' : 'su-dot--enc'"></span>
          <div class="flex-grow-1" style="min-width: 0;">
            <div class="d-flex justify-content-between" style="gap: 6px;">
              <b>{{ fmt(r.date) }}</b>
              <span>{{ money(r.amount) }}</span>
            </div>
            <small class="text-muted d-block text-truncate">
              <router-link v-if="r.source === 'claim' && !r.is_this_claim" :to="`/claim/${r.ref_id}`">Claim</router-link>
              <span v-else-if="r.source === 'claim'">This claim</span>
              <span v-else>Encounter</span>
              · qty {{ r.quantity }}{{ r.facility ? " · " + r.facility : "" }}
            </small>
          </div>
        </li>
      </ul>
    </template>
  </div>
</template>

<script>
import moment from "moment";

/** How often the claim's patient has had a line's service or drug (GET claims/{id}/service-usage), for the service modal. */
export default {
  props: {
    claimId: { type: [String, Number], required: true },
    lineId: { type: [String, Number], default: null },
  },
  data() { return { usage: null, loadedFor: null, loading: false, error: "" }; },
  computed: {
    year() { return this.usage ? this.usage.year : ""; },
    u() { return this.usage && this.lineId ? this.usage.lines.find((l) => String(l.line_id) === String(this.lineId)) || null : null; },
    years() {
      if (!this.u) return [];
      const by = this.u.by_year || {};
      const keys = Object.keys(by).map(Number);
      if (!keys.length) return [];
      const out = [];
      for (let y = Math.min(...keys); y <= Math.max(...keys, Number(this.year) || 0); y++) out.push({ year: y, n: by[y] || 0 });
      return out.slice(-8);
    },
    yearMax() { return Math.max(1, ...this.years.map((y) => y.n)); },
  },
  watch: {
    // Lines can be added or changed while the claim is open: reload when a line we haven't seen is opened.
    lineId() { if (this.lineId && (!this.u || this.loadedFor !== this.claimId)) this.load(); },
    claimId() { this.usage = null; this.load(); },
  },
  created() { if (this.lineId) this.load(); },
  methods: {
    load() {
      this.loading = true;
      this.error = "";
      this.axios.get(`/api/v1/auth/claims/${this.claimId}/service-usage`)
        .then((res) => { this.usage = res.data.data; this.loadedFor = this.claimId; })
        .catch((e) => { this.error = (e.response && e.response.data && e.response.data.message) || "Could not load the usage."; })
        .finally(() => { this.loading = false; });
    },
    money(v) { return "₦" + Math.round(Number(v) || 0).toLocaleString("en-US"); },
    fmt(d) { return d ? moment(d).format("D MMM YYYY") : ""; },
  },
};
</script>

<style scoped>
.su { border: 1px solid #e3eaf0; border-left: 4px solid #7c3aed; border-radius: 10px; padding: 12px 14px; margin-bottom: 1rem; background: #fff; }
.su-title { font-weight: 700; font-size: 0.85rem; color: #1e293b; margin-bottom: 10px; text-transform: none; }
.su-title::first-letter { text-transform: uppercase; }
.su-muted { color: #94a3b8; font-size: 0.85rem; }
.su-stats { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-bottom: 10px; }
.su-stat { display: flex; flex-direction: column; border-radius: 10px; padding: 10px 12px; background: #f8fafc; border: 1px solid #eef2f7; }
.su-stat--year { background: linear-gradient(135deg, #1e3a5f, #2563eb 70%, #7c3aed); border: 0; color: #fff; }
.su-label { font-size: 0.66rem; text-transform: uppercase; letter-spacing: 0.06em; font-weight: 700; opacity: 0.8; }
.su-num { font-size: 1.9rem; font-weight: 800; line-height: 1.1; }
.su-num small { font-size: 1rem; font-weight: 700; opacity: 0.7; margin-left: 1px; }
.su-sub { font-size: 0.72rem; opacity: 0.8; }
.su-table { width: 100%; font-size: 0.8rem; margin-bottom: 10px; }
.su-table th { font-size: 0.66rem; text-transform: uppercase; letter-spacing: 0.05em; color: #64748b; text-align: right; padding: 2px 0; }
.su-table td { padding: 3px 0; border-top: 1px solid #f1f5f9; text-align: right; font-weight: 600; color: #0f172a; }
.su-table td:first-child, .su-table th:first-child { text-align: left; color: #64748b; font-weight: 500; }
.su-flag { display: flex; gap: 8px; align-items: flex-start; font-size: 0.8rem; padding: 7px 10px; border-radius: 8px; background: #f1f5f9; color: #334155; margin-bottom: 6px; }
.su-flag i { margin-top: 2px; }
.su-flag--warn { background: #fffbeb; color: #92400e; box-shadow: inset 0 0 0 1px #fde68a; }
.su-flag--ok { background: #f0fdf4; color: #166534; }
.su-years { display: flex; align-items: flex-end; gap: 6px; height: 56px; margin: 8px 0 4px; padding: 0 2px; }
.su-year { display: flex; flex-direction: column; align-items: center; gap: 2px; flex: 1; }
.su-year-bar { width: 100%; max-width: 22px; border-radius: 4px 4px 0 0; background: #c7d2fe; }
.su-year-bar--now { background: #7c3aed; }
.su-year small { font-size: 0.62rem; color: #94a3b8; }
.su-sub-title { font-size: 0.66rem; text-transform: uppercase; letter-spacing: 0.06em; font-weight: 700; color: #64748b; margin: 10px 0 6px; }
.su-timeline { list-style: none; margin: 0; padding: 0 0 0 4px; border-left: 2px solid #eef2f7; }
.su-timeline li { display: flex; gap: 10px; align-items: flex-start; padding: 5px 0 5px 10px; position: relative; font-size: 0.8rem; }
.su-timeline li.is-this { background: #f5f3ff; border-radius: 8px; }
.su-dot { position: absolute; left: -9px; top: 10px; width: 12px; height: 12px; border-radius: 50%; border: 2px solid #fff; box-shadow: 0 0 0 1px #cbd5e1; }
.su-dot--claim { background: #7c3aed; }
.su-dot--enc { background: #2563eb; }
</style>
